import {
  EntityPermissionOperationNames,
  EntityType,
  IEntityPermission
} from "@amzn/ask-legal-domain";
import { AppLayout, AppLayoutProps, Spinner } from "@amzn/awsui-components-react";
import { Hub } from "aws-amplify";
import { Builder } from "builder-pattern";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { CookiesProvider } from "react-cookie";
import { HashRouter, Route, Routes } from "react-router-dom";
import legalAIIcon from "../src/components/chatbot/legal-ai-companion-icon.svg";
import { SpoofIdentityBanner } from "./components/common/SpoofIdentityBanner";
import {
  AiCompanionContentPlaceholderComponent,
  LegalAiCompanionComponent
} from "./components/chatbot/legal-ai-companion";
import { useStyles } from "./components/chatbot/legal-ai-companion-floating-button/styles";
import { useInstanceState } from "./components/instance/instance-store";
import { AskLegalLayout } from "./components/layout/AskLegalLayout";
import { DrawerProvider } from "./components/layout/DrawerContext";
import { InstanceDataProvider } from "./contexts/InstanceDataContext";
import { TitleProvider } from "./contexts/TitleContext";
import { commonRoutes } from "./pages/common";
import { decisionTreeRoutes } from "./pages/decision-tree";
import { instanceRoutes } from "./pages/instance";
import { legalContactRoutes } from "./pages/legal-contact";
import { pageRoutes } from "./pages/page";
import { pageLibraryRoutes } from "./pages/page-library";
import { recommendationRoutes } from "./pages/recommendation";
import { redirectRoutes } from "./pages/redirects";
import { searchRoutes } from "./pages/search";
import { sharedPageRoutes } from "./pages/shared-page";
import { devopsRoutes } from "./pages/devops";
import { AppContext } from "./setup/context";
import { extractInstanceFromParams } from "./setup/useRoutedParamsWithContext";
import "./styles/component/chatbot/chatbot.scss";
import "./styles/component/layout/AskLegalLayout.scss";
import { sleep } from "./utils/common-utils";

const ORIGINAL_URL_STORAGE_KEY = "OrignalURLHashBeforeAuth";
const CHATBOT_POPUP_SESSION_KEY = "show-chatbot-popup";
const AUTH_TOKEN_WAIT_MS = 500;

export interface RouteDefinition {
  path: string;
  content: (() => JSX.Element) | JSX.Element;
  header: () => JSX.Element;
  footer?: () => JSX.Element;
  breadcrumbs?: () => JSX.Element;
  contentClass?: string;
}
enum AuthStatus {
  Authenticated,
  UnAuthenticated,
  Error
}
enum DrawerType {
  LegalAi = "LEGAL_AI",
}

const routes: RouteDefinition[] = [
  ...commonRoutes,
  ...instanceRoutes,
  ...pageLibraryRoutes,
  ...pageRoutes,
  ...searchRoutes,
  ...recommendationRoutes,
  ...decisionTreeRoutes,
  ...legalContactRoutes,
  ...sharedPageRoutes,
  ...devopsRoutes,
  ...redirectRoutes
];

const getResponsiveDrawerSize = () => {
  const viewportWidth = window.innerWidth;
  // Use max 40% of viewport width, min 350px, max 550px
  const calculatedSize = Math.min(Math.max(viewportWidth * 0.4, 350), 550);
  return Math.floor(calculatedSize);
};

let drawerResizeCallback: ((size: number) => void) | undefined;

const chatbotDrawer: AppLayoutProps.Drawer = {
  id: DrawerType.LegalAi,
  resizable: true,
  content: <AiCompanionContentPlaceholderComponent />,
  defaultSize: getResponsiveDrawerSize(),
  onResize: (event) => {
    if (drawerResizeCallback) {
      drawerResizeCallback(event.detail.size);
    }
  },
  ariaLabels: {
    drawerName: "Ai companion",
    closeButton: "Close",
    resizeHandle: "Resize",
  },
  trigger: {
    iconName: "star-filled",
  },
};

const isOAuthCallback = (url: string): boolean =>
  url.includes("code=") || url.includes("state=") || url.includes("access_token=");

const isDefaultRoute = (hash: string): boolean =>
  !hash || hash === "#/" || hash === "#" || hash === "#/home";

export const App = () => {
  const context = React.useContext(AppContext);
  const { instanceId } = useInstanceState();
  const { floatingButton } = useStyles();
  const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.UnAuthenticated);
  const [showChatbotIcon, setShowChatbotIcon] = useState(false);
  const [showChatbotDrawer, setShowChatbotDrawer] = useState(false);
  const [showAppLayoutDrawer, setShowAppLayoutDrawer] = useState(false);
  const [activeDrawerId, setActiveDrawerId] = useState<string | null>(null);
  const [drawerSize, setDrawerSize] = useState(getResponsiveDrawerSize());

  const buttonRef = useRef<HTMLButtonElement>(null);

  const extractInstanceFromRoute = (route: string): string | null => {
    try {
      const path = route.replace(/^#/, "");
      const directInstanceRoutes = ["instance", "search", "legal-contact", "recommendation", "shared-page"];
      const directInstancePattern = new RegExp(`^\\/(${directInstanceRoutes.join("|")})\\/([^/]+)`);
      const directMatch = path.match(directInstancePattern);
      const indirectRoutes = {
        "page": "pageId",
        "page-library": "pageLibraryId",
        "decision-tree": "containerId"
      };

      const params: Record<string, string | undefined> = {};
      if (directMatch) {
        params.instanceId = directMatch[2];
      } else {
        for (const [routeType, paramName] of Object.entries(indirectRoutes)) {
          const match = path.match(new RegExp(`^\\/${routeType}\\/([^/]+)`));
          if (match) {
            params[paramName] = match[1];
            break;
          }
        }
      }

      return extractInstanceFromParams(params);
    } catch (e) {
      console.warn("Error extracting instance from route:", route, e);
      return null;
    }
  };

  const autoSetInstancePreference = async (): Promise<void> => {
    try {
      const extractedInstanceId = extractInstanceFromRoute(window.location.hash);
      if (extractedInstanceId) {
        const identity = await context.getIdentity();
        const currentPreference = context.getPreference();
        if (!currentPreference || currentPreference.userId !== identity.id || !currentPreference.instanceId) {
          await context.setPreference(extractedInstanceId);
        }
      }
    } catch (e) {
      console.warn("Error auto-setting instance preference:", e);
    }
  };

  const handleSignIn = () => onSignedIn();
  const handleSignInFailure = (message: string) => {
    console.debug(`Sign-in failed: ${message}`);
    setAuthStatus(AuthStatus.Error);
  };

  const onSignedIn = async (): Promise<void> => {
    await context.getIdentity();
    setAuthStatus(AuthStatus.Authenticated);
    await autoSetInstancePreference();
    const storedRoute = localStorage.getItem(ORIGINAL_URL_STORAGE_KEY);
    if (storedRoute) {
      localStorage.removeItem(ORIGINAL_URL_STORAGE_KEY);
      if (storedRoute.startsWith("#/")) {
        window.location.replace(`${window.location.origin}${window.location.pathname}${storedRoute}`);
      } else if (storedRoute.startsWith("http")) {
        try {
          const storedUrl = new URL(storedRoute);
          const currentUrl = new URL(window.location.href);
          if (storedUrl.origin === currentUrl.origin) {
            window.location.replace(storedRoute);
          } else {
            console.warn("Stored URL has different origin, ignoring:", storedRoute);
          }
        } catch (e) {
          console.warn("Invalid stored URL, ignoring:", storedRoute);
        }
      }
    }
  };

  const initializeAuth = async (): Promise<void> => {
    try {
      Hub.listen("auth", (data) => {
        switch (data.payload.event) {
          case "signIn":
            handleSignIn();
            break;
          case "signIn_failure":
            handleSignInFailure(data.payload.message);
            break;
        }
      });
      await context.init();
      await sleep(AUTH_TOKEN_WAIT_MS); // Wait for auth tokens
      await context.currentUser();


      await onSignedIn();
    } catch (err) {
      console.warn(err);
      const currentUrl = window.location.href;
      const currentHash = window.location.hash;
      const hasStoredUrl = localStorage.getItem(ORIGINAL_URL_STORAGE_KEY);
      if (!isOAuthCallback(currentUrl) && !hasStoredUrl && !isDefaultRoute(currentHash)) {
        localStorage.setItem(ORIGINAL_URL_STORAGE_KEY, currentHash);
      }
      await context.signIn();
    }
  };

  const isUserAdmin = async (instanceId: string): Promise<boolean> => {
    try {
      const result = await context.getEntityPermissionAPI().isAuthorized(
        Builder<IEntityPermission.IsAuthorizedInput>()
          .id(instanceId)
          .type(EntityType.Instance)
          .permission(EntityPermissionOperationNames.CAN_ADMIN)
          .build()
      );
      return (result.data as any).data;
    } catch (e) {
      console.error("Error checking admin permission:", e);
      return false;
    }
  };

  const isUserMemberOfRole = async (roleId: string): Promise<boolean> => {
    try {
      const result = await context.getEntityPermissionAPI().isAuthorized(
        Builder<IEntityPermission.IsAuthorizedInput>()
          .id(roleId)
          .type(EntityType.Role)
          .permission(EntityPermissionOperationNames.CAN_VIEW)
          .build()
      );
      return (result.data as any).data;
    } catch (e) {
      console.error("Error checking role membership:", e);
      return false;
    }
  };

  const updateChatbotVisibility = async (instanceId: string): Promise<void> => {
    try {
      const instanceConfig = context.config.legalAICompanion?.instancesConfig?.find(
        config => config.instanceId.toLowerCase() === instanceId.toLowerCase()
      );
      if (!instanceConfig) {
        setShowChatbotIcon(false);
        setActiveDrawerId(null);
        setShowChatbotDrawer(false);
        return;
      }
      const permissionType = instanceConfig.permissionConfg?.type?.toLowerCase();
      let shouldShow = false;
      switch (permissionType) {
        case "instance-role":
          shouldShow = await isUserMemberOfRole(instanceConfig.permissionConfg?.value);
          break;
        case "instance-admin":
          shouldShow = await isUserAdmin(instanceId);
          break;
        case "instance-view":
          shouldShow = true;
          break;
      }
      setShowChatbotIcon(shouldShow);
    } catch (e) {
      console.error("Error checking chatbot visibility:", e);
      setShowChatbotIcon(false);
    }
  };

  const handleDrawerChange = (drawerEvent: any) => {
    const isOpening = drawerEvent.detail.activeDrawerId === DrawerType.LegalAi;
    const isDrawerActive = drawerEvent.detail.activeDrawerId === DrawerType.LegalAi || activeDrawerId === DrawerType.LegalAi;
    setShowChatbotDrawer(isOpening);
    setShowChatbotIcon(!isOpening);
    setActiveDrawerId(isOpening ? drawerEvent.detail.activeDrawerId : null);
    if (drawerEvent.detail.size && isDrawerActive) {
      setDrawerSize(drawerEvent.detail.size);
    }
  };
  const handleChatbotButtonClick = () => {
    setShowChatbotDrawer(true);
    setShowChatbotIcon(false);
  };

  const renderRoutes = () => (
    <Routes>
      {routes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <div className="awsui">
              <AskLegalLayout
                header={<route.header />}
                content={
                  <div className={route.contentClass || "sherpa-content"}>
                    {route.breadcrumbs && <route.breadcrumbs />}
                    {typeof route.content === "function" ? route.content() : route.content}
                  </div>
                }
                footer={route.footer ? <route.footer /> : null}
              />
            </div>
          }
        />
      ))}
    </Routes>
  );

  useEffect(() => {
    initializeAuth();
  }, []);
  useEffect(() => {
    drawerResizeCallback = (size: number) => {
      setDrawerSize(size);
    };
    return () => {
      drawerResizeCallback = undefined;
    };
  }, []);
  useEffect(() => {
    setShowAppLayoutDrawer(showChatbotDrawer);
    if (showChatbotDrawer) {
      setActiveDrawerId(DrawerType.LegalAi);
    }
  }, [showChatbotDrawer]);
  useEffect(() => {
    if (instanceId) {
      updateChatbotVisibility(instanceId);
    }
  }, [instanceId]);

  if (authStatus === AuthStatus.UnAuthenticated) {
    return (
      <span className="awsui-util-status-inactive vertical-center horizontal-center" style={{ paddingTop: "25px" }}>
        <Spinner size="large" /> Loading Resources
      </span>
    );
  }
  if (authStatus === AuthStatus.Error) {
    return (
      <div>
        Failed to load. Please try to refresh the page. If the issue still persists after multiple tries,
        please contact altar-dev@amazon.com for support.
      </div>
    );
  }

  return (
    <CookiesProvider>
      <TitleProvider>
        <AppContext.Provider value={context}>
          <AppLayout
            toolsHide
            drawers={showAppLayoutDrawer ? [chatbotDrawer] : []}
            activeDrawerId={activeDrawerId}
            onDrawerChange={handleDrawerChange}
            navigationHide
            disableContentPaddings
            content={
              <>
                <SpoofIdentityBanner />
                <div>
                  <LegalAiCompanionComponent />
                  {showChatbotIcon && !activeDrawerId && (
                    <>
                      <button
                        type="button"
                        className={floatingButton}
                        onClick={handleChatbotButtonClick}
                        aria-label="Legal AI Companion"
                        ref={buttonRef}
                      >
                        <img src={legalAIIcon} alt="Legal AI" />
                      </button>
                    </>
                  )}
                </div>
                <InstanceDataProvider>
                  <DrawerProvider
                    value={{
                      isDrawerOpen: activeDrawerId === DrawerType.LegalAi,
                      drawerSize: activeDrawerId === DrawerType.LegalAi ? drawerSize : 0
                    }}
                  >
                    <HashRouter>{renderRoutes()}</HashRouter>
                  </DrawerProvider>
                </InstanceDataProvider>
              </>
            }
          />
        </AppContext.Provider>
      </TitleProvider>
    </CookiesProvider>
  );
};
